import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';
import {
  MIXPANEL_EVENTS,
  listingMixpanelProperties,
  trackMixpanelEvent,
} from '../../util/mixpanel';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

import { optimizeCloudinaryUrl } from '../../util/cloudinaryHelpers';

// Static fallback copy used when listing data is not available.
const STATIC_CATEGORY = 'HERITAGE';
const STATIC_TITLE = 'Title of this golden experience comes here';
const STATIC_DESCRIPTION = 'This is a one liner emotional hook line';

const SparkleIcon = () => (
  <svg
    className={css.sparkleIcon}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M10 0c.4 4.9 4.7 9.2 9.6 9.6v.8C14.7 10.8 10.4 15.1 10 20h-.8C8.8 15.1 4.5 10.8-.4 10.4v-.8C4.5 9.2 8.8 4.9 9.2 0H10z"
      fill="currentColor"
    />
  </svg>
);

const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    isFirstCard,
  } = props;

  const publicData = listing?.attributes?.publicData || {};
  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const heroImageCloudinaryUrl = publicData?.heroImageCloudinaryUrl;

  return (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      {heroImageCloudinaryUrl ? (
        <img
          src={optimizeCloudinaryUrl(heroImageCloudinaryUrl, 800)}
          srcSet={`
            ${optimizeCloudinaryUrl(heroImageCloudinaryUrl, 400)} 400w,
            ${optimizeCloudinaryUrl(heroImageCloudinaryUrl, 600)} 600w,
            ${optimizeCloudinaryUrl(heroImageCloudinaryUrl, 800)} 800w,
            ${optimizeCloudinaryUrl(heroImageCloudinaryUrl, 1200)} 1200w
          `}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={title}
          className={css.rootForImage}
          loading={isFirstCard ? 'eager' : 'lazy'}
          decoding="async"
        />
      ) : (
        <ResponsiveImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      )}
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 * Displays a listing in a card layout with a full-width image, category tag,
 * title, description, and DISCOVER / RESERVE action links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card used in the search results panel
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    goldenListing,
    analyticsSource = 'listing_card',
    analyticsPosition,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const { titlePlain, titleFormatted, cardAriaLabel } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData, description } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle, categoryLevel1, heroImageCloudinaryUrl } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(listing?.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  const discoverLabel = intl.formatMessage({ id: 'ListingCard.discover' });
  const reserveLabel = intl.formatMessage({ id: 'ListingCard.reserve' });
  const listingPageLinkProps = {
    name: 'ListingPage',
    params: { id, slug },
    ariaLabel: cardAriaLabel,
  };
  const trackCardClick = action => {
    trackMixpanelEvent(MIXPANEL_EVENTS.LISTING_CARD_CLICKED, {
      ...listingMixpanelProperties(listing),
      source: analyticsSource,
      position: Number.isInteger(analyticsPosition) ? analyticsPosition + 1 : null,
      action,
    });
  };

  // Static fallbacks so the card matches the reference design even without data.
  const categoryText =
    publicData?.screen_tag && typeof publicData.screen_tag === 'string'
      ? publicData.screen_tag.toUpperCase()
      : categoryLevel1
        ? categoryLevel1.toUpperCase()
        : STATIC_CATEGORY;
  const titleContent = titleFormatted || STATIC_TITLE;
  const descriptionContent = description || STATIC_DESCRIPTION;
  const subTitle = publicData.subtitle;

  const rawListingNum = publicData?.listingNumber ?? publicData?.screen_number;
  const listingNumParsed =
    rawListingNum != null && !isNaN(parseInt(rawListingNum, 10))
      ? parseInt(rawListingNum, 10)
      : null;

  const displayNumber =
    listingNumParsed != null
      ? listingNumParsed
      : Number.isInteger(analyticsPosition)
        ? goldenListing
          ? analyticsPosition + 1
          : analyticsPosition + 6
        : null;

  return (
    <div
      className={classNames(classes, goldenListing ? css.goldenListingImg : css.srcPageListingCard)}
      {...setActivePropsMaybe}
    >
      {/* Image section */}
      <div className={css.imageWrapper}>
        <span className={css.categoryTag}>{categoryText}</span>

        <NamedLink
          className={css.imageLink}
          {...listingPageLinkProps}
          onClick={() => trackCardClick('discover')}
        >
          {showListingImage || heroImageCloudinaryUrl ? (
            <ListingCardImage
              renderSizes={renderSizes}
              title={titlePlain}
              listing={listing}
              setActivePropsMaybe={null}
              aspectWidth={aspectWidth}
              aspectHeight={aspectHeight}
              variantPrefix={variantPrefix}
              isFirstCard={analyticsPosition === 0}
            />
          ) : (
            <ListingCardThumbnail
              style={cardStyle}
              listingTitle={title}
              className={css.aspectRatioWrapper}
              width={aspectWidth}
              height={aspectHeight}
              setActivePropsMaybe={null}
            />
          )}

          {/* Gradient overlay to keep tag / icon legible over the image */}
          <div className={css.imageOverlay} />
        </NamedLink>
      </div>

      {/* Info section */}
      <div className={css.info}>
        <div className={css.mainInfo}>
          <h3 className={css.title}>{titleContent}</h3>
          <p className={css.description}>{subTitle}</p>
        </div>

        {/* CTA links */}
        <div className={css.actions}>
          <NamedLink
            className={css.actionLink}
            {...listingPageLinkProps}
            onClick={() => trackCardClick('discover')}
          >
            {discoverLabel}
          </NamedLink>
          <NamedLink
            className={css.actionLink}
            name="BookingFlowPage"
            params={{ id, slug }}
            ariaLabel={cardAriaLabel}
            onClick={() => trackCardClick('reserve')}
          >
            {reserveLabel}
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
